import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { Recipe } from "@/client/api";
import { Badge } from "@/client/components/ui/index";
import { DropdownIcon } from "@/client/icons";
import { normalize } from "@/client/utils";

const TYPE_ORDER = ["main", "side", "base", "dessert", "other"];
const TYPE_LABELS: Record<string, string> = {
  main: "Principales",
  side: "Guarniciones",
  base: "Bases",
  dessert: "Postres",
  other: "Otras",
};

export function RecipeSelect({
  recipes,
  value,
  onChange,
  placeholder = "Buscar receta...",
  filterFn,
}: {
  recipes: Recipe[];
  value: string;
  onChange: (recipeId: string) => void;
  placeholder?: string;
  filterFn?: (r: Recipe) => boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const pool = filterFn ? recipes.filter(filterFn) : recipes;
  const filtered = pool.filter((r) => normalize(r.name).includes(normalize(search)));
  const byType: Record<string, Recipe[]> = {};
  for (const r of filtered) (byType[r.type ?? "other"] ??= []).push(r);
  // Flat ordered list for keyboard navigation
  const flatList = TYPE_ORDER.flatMap((t) => byType[t] ?? []);

  const selected = recipes.find((r) => r.id === value);

  function updatePos() {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }

  useLayoutEffect(() => {
    if (open) updatePos();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  function handleOpen() {
    setOpen(true);
    setSearch("");
    setHighlightIdx(0);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, flatList.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flatList[highlightIdx]) handlePick(flatList[highlightIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  function handlePick(r: Recipe) {
    onChange(r.id);
    setOpen(false);
    setSearch("");
  }

  const dropdown = open
    ? ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width }}
          className="z-60 bg-term-surface border border-term-border rounded-lg shadow-lg max-h-64 overflow-y-auto"
        >
          {TYPE_ORDER.filter((t) => byType[t]?.length).map((t) => (
            <div key={t}>
              <div className="text-xs font-medium text-term-text-muted uppercase tracking-wider px-3 pt-2.5 pb-1">
                {TYPE_LABELS[t]}
              </div>
              {byType[t]!.map((r) => {
                const idx = flatList.indexOf(r);
                const isHighlighted = idx === highlightIdx;
                return (
                  <button
                    key={r.id}
                    ref={
                      isHighlighted ? (el) => el?.scrollIntoView({ block: "nearest" }) : undefined
                    }
                    onClick={() => handlePick(r)}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    className={`flex items-center gap-3 px-3 py-2 text-left cursor-pointer transition-colors text-sm w-full ${
                      isHighlighted
                        ? "bg-term-raised text-term-text"
                        : "text-term-text hover:bg-term-raised/50"
                    }`}
                  >
                    {r.type && <Badge value={r.type} />}
                    <span className="flex-1">{r.name}</span>
                  </button>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-sm text-term-text-muted py-4">Sin resultados</p>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={containerRef}>
      {/* Trigger */}
      {!open ? (
        <button
          type="button"
          onClick={handleOpen}
          className="w-full px-3 py-2 text-sm bg-term-bg border border-term-border rounded-lg text-left focus:border-term-accent-dim focus:outline-none transition-colors cursor-pointer flex items-center gap-2"
        >
          {selected ? (
            <>
              {selected.type && <Badge value={selected.type} />}
              <span className="text-term-text flex-1">{selected.name}</span>
            </>
          ) : (
            <span className="text-term-text-muted flex-1">{placeholder}</span>
          )}
          <DropdownIcon width={14} height={14} className="text-term-text-muted shrink-0" />
        </button>
      ) : (
        <input
          ref={inputRef}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setHighlightIdx(0);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full px-3 py-2 text-sm bg-term-bg border border-term-accent-dim rounded-lg text-term-text focus:outline-none transition-colors"
        />
      )}
      {dropdown}
    </div>
  );
}
